import { useMemo } from "react";
import { buildHourlySeriesFromMinuteArray } from "../utils/hourlySeries";

/* --------------------------------
   Helpers
-------------------------------- */

function isMinuteArray(v) {
  return Array.isArray(v) && v.length === 1440;
}

function buildHourlyAvgFromMinutesObject(obj) {
  if (!obj || typeof obj !== "object") return [];

  return Array.from({ length: 24 }, (_, h) => {
    const rawMinutes =
      Number(obj[String(h).padStart(2, "0")]) || 0;

    return {
      label: `${String(h).padStart(2, "0")}:00`,
      value: Math.round(rawMinutes / 60), // ✅ FIX
    };
  });
}

/* --------------------------------
   Hook
-------------------------------- */

export function useYesterdayMetrics(
  rawVideo,
  rawImpressions,
  yesterdayKey,
  yesterdayImpKey
) {
  /* -----------------------------
     Avg Active TV (Yesterday)
     ALWAYS AVERAGE (÷ 60)
  ----------------------------- */
  const avgActiveYesterday = useMemo(() => {
    if (!rawVideo || !yesterdayKey) return [];

    const data = rawVideo[yesterdayKey];
    if (!data) return [];

    // minute-based (1440)
    if (isMinuteArray(data)) {
      const hourly = buildHourlySeriesFromMinuteArray(data, true);
      return hourly.map((p) => ({
        ...p,
        value: Math.round((Number(p.value) || 0) / 60), // ✅ FIX
      }));
    }

    // hourly minutes object
    if (typeof data === "object") {
      return buildHourlyAvgFromMinutesObject(data);
    }

    return [];
  }, [rawVideo, yesterdayKey]);

  /* -----------------------------
     Switch-In (COUNTS, no ÷ 60)
  ----------------------------- */
  const switchInYesterday = useMemo(() => {
    if (!rawImpressions || !yesterdayImpKey) return [];

    const data = rawImpressions[yesterdayImpKey];
    if (!data) return [];

    if (isMinuteArray(data)) {
      return buildHourlySeriesFromMinuteArray(data, false);
    }

    if (typeof data === "object") {
      return Array.from({ length: 24 }, (_, h) => ({
        label: `${String(h).padStart(2, "0")}:00`,
        value: Number(data[String(h).padStart(2, "0")]) || 0,
      }));
    }

    return [];
  }, [rawImpressions, yesterdayImpKey]);

  /* -----------------------------
     TOTALS
  ----------------------------- */
  const avgActiveTotal = useMemo(() => {
    if (!avgActiveYesterday.length) return 0;
    return avgActiveYesterday.reduce(
      (sum, p) => sum + (Number(p.value) || 0),
      0
    );
  }, [avgActiveYesterday]);

  const switchInTotal = useMemo(() => {
    if (!switchInYesterday.length) return 0;
    return switchInYesterday.reduce(
      (sum, p) => sum + (Number(p.value) || 0),
      0
    );
  }, [switchInYesterday]);

  return {
    avgActiveYesterday,
    switchInYesterday,
    avgActiveTotal,
    switchInTotal,
  };
}
